import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { IBed } from './ibed';
import { MatPaginator } from '@angular/material/paginator';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, switchMap, takeUntil } from 'rxjs';
import { PaginationResult } from 'src/app/models/common.response';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { LookupService } from 'src/app/services/lookup.service';
import { MatDialog } from '@angular/material/dialog';
import { BedService } from './bed.service';
import { BedDetailComponent } from './bed-detail/bed-detail.component';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';
import { EmptyStateComponent } from 'src/app/shared/empty-state/empty-state.component';


@Component({
  selector: 'app-bed',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
    EmptyStateComponent
  ],
  templateUrl: './bed.component.html',
  styleUrl: './bed.component.scss'
})
export class BedComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() roomId: number | null = null;

  displayedColumns: string[] = [
    'bedNumber',
    'roomNumber',
    'status',
    'isActive',
    'action',
  ];

  dataSource = new MatTableDataSource<IBed>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  pagination: PaginationResult;
  bedcount: number = 0;
  beddata: IBed[];
  isLoading: boolean = false;
  searchBed: FormControl = new FormControl();
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  roomList: CommonDropDown[];
  selectedRoomNumber: string = '';

  constructor(
    private _bedService: BedService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private _lookupService: LookupService,
  ) { }

  ngOnInit(): void {
    this._bedService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.bedcount = this.pagination.rowCount;
        }
      });

    this._bedService.beds$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.beddata = data;
        this.dataSource = new MatTableDataSource(this.beddata); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });

    this.searchBed.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        const requestPayload: any = {
          pageNumber: 0,
          pageSize: this.pagination.pageSize || 10,
          isDescending: false,
          searchValue: query || ''
        };
        if (this.roomId !== null && this.roomId !== undefined) {
          requestPayload.roomId = this.roomId;
        }
        return this._bedService.getAllBeds(requestPayload).pipe(
          finalize(() => (this.isLoading = false)),
          catchError((error) => {
            this.toastr.error(this.translate.instant('COMMON.ERROR'));
            console.error('Error searching beds:', error);
            return of(null);
          })
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.bedDetails;
        this.bedcount = response.result.pagination.rowCount;
      }
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['roomId'] && !changes['roomId'].firstChange) {
      if (this.roomId !== null && this.roomId !== undefined) {
        const room = this.roomList?.find(r => r.id === this.roomId);
        this.selectedRoomNumber = room ? room.name : '';
        this.refreshBed();
      } else {
        this.selectedRoomNumber = '';
        this.refreshBed();
      }
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onAddBed() {
    this.dialog.open(BedDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
      data: {
        roomId: this.roomId
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshBed();
      }
    });
  }

  onUpdateBed(element: IBed) {
    this.dialog.open(BedDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
      data:
      {
        bed: element,
        isEdit: true
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshBed();
      }
    });
  }

  onDeleteBed(bedId: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.BED') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._bedService.deleteBed(bedId).subscribe((response) => {
          if (response.isSuccess) {
            this.refreshBed();
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.BED')));
          }
          else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          }
        })
      }
    });

  }

  refreshBed() {
    const globalSearchValue = this.searchBed?.value?.trim();
    const requestPayload: any = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.SearchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    if (this.roomId !== null && this.roomId !== undefined) {
      requestPayload.roomId = this.roomId;
    }

    this._bedService.getAllBeds(requestPayload)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.toastr.error(this.translate.instant('COMMON.ERROR'));
          console.error('Error refreshing beds:', error);
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response && response.result) {
          const Bed = response.result.bedDetails;
          this.beddata = Bed;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(Bed);
          } else {
            this.dataSource.data = Bed;
          }
          this.dataSource.paginator = this.paginator;
          this.bedcount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });

  }

}
